import { useMemo } from 'react';

import type { TransmissionEvent as TransmissionEventType } from '../../../data/utils/types';
import { useGetTransmissionEvents } from '../../../hooks/useGetTransmissionEvents';
import { TransmissionEvent } from '../types';

interface UseTransmissionEventsResult {
  transmissionEvents: TransmissionEvent[];
  loading: boolean;
  error: Error | null;
}

/**
 * Transform TransmissionEvent from utils/types to OrderDetails types
 */
const transformTransmissionEvent = (
  event: TransmissionEventType
): TransmissionEvent => {
  return {
    id: event.id,
    ofId: event.ofId,
    ofIdType: event.ofIdType || '',
    to: event.to || '',
    toType: event.toType || '',
    newState: event.newState || '',
    isDeliveryEvent: event.isDeliveryEvent || false,
    isFailureEvent: event.isFailureEvent || false,
    timestamp: event.timestamp,
    metaData: event.metaData || null,
  };
};

/**
 * Custom hook to fetch transmission events for an order using GraphQL
 */
export const useTransmissionEvents = (
  orderId: string | null
): UseTransmissionEventsResult => {
  const {
    transmissionEvents: rawEvents,
    loading,
    error,
  } = useGetTransmissionEvents(orderId || '', undefined);

  const transmissionEvents = useMemo(() => {
    if (!rawEvents || rawEvents.length === 0) return [];
    return rawEvents.map(transformTransmissionEvent);
  }, [rawEvents]);

  return { transmissionEvents, loading, error };
};
